import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    TextField,
    Stack,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Alert,
    Divider,
} from '@mui/material';
import { Close as CloseIcon, Receipt as ReceiptIcon, HelpOutline as HelpIcon } from '@mui/icons-material';

export const CONSULTAR_FATURAS_DEFAULTS = {
    quantidade: 3,
    statusFilter: 'abertas',
    sendMode: 'dados',
    enviarPdf: false,
    maxCpfAttempts: 3,
    limitarDiasVencidas: false,
    maxDiasVencidas: 90,
    listIntroMessage: '📋 Suas faturas em aberto:',
    askCpfMessage: 'Para consultar suas faturas, informe seu CPF ou CNPJ:',
    cpfNotFoundMessage: 'Nao encontramos cadastro com este CPF/CNPJ. Verifique e tente novamente.',
    confirmIdentityMessage: 'Encontramos o cadastro: *{{nome_cliente}}*\nEste e o seu cadastro?\n1️⃣ - Sim\n2️⃣ - Nao',
    identityDeniedMessage: 'Entendido.\n1️⃣ - Informar CPF/CNPJ novamente\n2️⃣ - Aguardar atendimento',
    askLinkConsentMessage: 'Podemos utilizar este CPF/CNPJ para identifica-lo automaticamente nas proximas vezes que entrar em contato?\n1️⃣ - Sim\n2️⃣ - Nao',
    noInvoicesMessage: '✅ Nenhuma fatura encontrada para seu cadastro.',
    faturasExpiradasMessage: '⚠️ Encontramos pendências que precisam de análise da nossa equipe.\n\n⏳ Seu atendimento será transferido para um de nossos atendentes.',
    semDadosPagamentoMessage: '⚠️ Não foi possível localizar os dados de pagamento desta fatura.\n\n⏳ Seu atendimento está sendo transferido para um atendente.',
    invalidChoiceMessage: '❌ Opcao invalida. Informe o número, ou o mês de referência da fatura',
    mensagem1: '',
    mensagem2: '',
    mensagem3: '',
    mensagem4: 'Enviei os dados da fatura para pagamento, se tiver alguma duvida so nos chamar',
};

interface FlowBuilderConsultarFaturasModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: Record<string, any>) => void;
    data?: Record<string, any>;
}

const FlowBuilderConsultarFaturasModal = ({
    open,
    onClose,
    onSave,
    data,
}: FlowBuilderConsultarFaturasModalProps) => {
    const [activeTab, setActiveTab] = useState(0);
    const [helpOpen, setHelpOpen] = useState(false);
    const [form, setForm] = useState<Record<string, any>>({ ...CONSULTAR_FATURAS_DEFAULTS, label: 'Consultar Faturas' });

    useEffect(() => {
        if (open) {
            setForm({ ...CONSULTAR_FATURAS_DEFAULTS, label: 'Consultar Faturas', ...data });
            setActiveTab(0);
        }
    }, [open, data]);

    useEffect(() => {
        if (form.sendMode === 'link' && activeTab > 2) {
            setActiveTab(0);
        }
    }, [form.sendMode, activeTab]);

    const update = (patch: Record<string, any>) => {
        setForm((prev) => ({ ...prev, ...patch }));
    };

    const handleSave = () => {
        onSave(form);
        onClose();
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ color: '#5e35b1' }} />
                    <Typography variant="h6" component="span">Consultar Faturas</Typography>
                    <IconButton size="small" onClick={() => setHelpOpen(true)} title="Como funciona este no" sx={{ color: '#5e35b1' }}>
                        <HelpIcon fontSize="small" />
                    </IconButton>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Alert severity="info" sx={{ mb: 0 }}>
                        Saidas do no: Sem cliente · OK · Sem faturas · Sem solucao
                    </Alert>

                    <TextField
                        label="Nome do no"
                        fullWidth
                        size="small"
                        value={form.label || ''}
                        onChange={(e) => update({ label: e.target.value })}
                    />

                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
                        <Tab label="Geral" />
                        <Tab label="Identificacao" />
                        <Tab label="Faturas" />
                        {form.sendMode !== 'link' && <Tab label="Modo dados" />}
                    </Tabs>

                    {activeTab === 0 && (
                        <Stack spacing={2}>
                            <TextField
                                label="Quantidade de faturas na lista"
                                type="number"
                                fullWidth
                                size="small"
                                inputProps={{ min: 1, max: 20 }}
                                value={form.quantidade ?? 3}
                                onChange={(e) => update({ quantidade: parseInt(e.target.value, 10) || 3 })}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Filtro de status</InputLabel>
                                <Select
                                    value={form.statusFilter || 'abertas'}
                                    label="Filtro de status"
                                    onChange={(e) => update({ statusFilter: e.target.value })}
                                >
                                    <MenuItem value="abertas">Abertas (aberto + vencido)</MenuItem>
                                    <MenuItem value="aberto">Somente aberto</MenuItem>
                                    <MenuItem value="vencido">Somente vencido</MenuItem>
                                    <MenuItem value="todas">Todas (exceto canceladas)</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Modo de envio</InputLabel>
                                <Select
                                    value={form.sendMode || 'dados'}
                                    label="Modo de envio"
                                    onChange={(e) => update({ sendMode: e.target.value })}
                                >
                                    <MenuItem value="dados">Dados da fatura (linha digitavel + PIX)</MenuItem>
                                    <MenuItem value="link">Link (template enviar_fatura)</MenuItem>
                                </Select>
                            </FormControl>
                            {form.sendMode === 'link' && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(form.enviarPdf)}
                                            onChange={(e) => update({ enviarPdf: e.target.checked })}
                                        />
                                    }
                                    label="Anexar PDF da fatura (se template permitir)"
                                />
                            )}
                            <TextField
                                label="Max tentativas CPF"
                                type="number"
                                fullWidth
                                size="small"
                                inputProps={{ min: 1, max: 10 }}
                                value={form.maxCpfAttempts ?? 3}
                                onChange={(e) => update({ maxCpfAttempts: parseInt(e.target.value, 10) || 3 })}
                            />
                            <Divider />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(form.limitarDiasVencidas)}
                                        onChange={(e) => update({ limitarDiasVencidas: e.target.checked })}
                                    />
                                }
                                label="Nao enviar faturas com mais de X dias vencidas"
                            />
                            {form.limitarDiasVencidas && (
                                <TextField
                                    label="Maximo de dias vencidas"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 1, max: 3650 }}
                                    value={form.maxDiasVencidas ?? 90}
                                    onChange={(e) => update({ maxDiasVencidas: parseInt(e.target.value, 10) || 90 })}
                                    helperText="Se todas as faturas ultrapassarem este limite, o fluxo segue pela saida Sem solucao."
                                />
                            )}
                        </Stack>
                    )}

                    {activeTab === 1 && (
                        <Stack spacing={2}>
                            <TextField label="Pedir CPF/CNPJ" fullWidth multiline rows={2} size="small" value={form.askCpfMessage || ''} onChange={(e) => update({ askCpfMessage: e.target.value })} />
                            <TextField label="CPF nao encontrado" fullWidth multiline rows={2} size="small" value={form.cpfNotFoundMessage || ''} onChange={(e) => update({ cpfNotFoundMessage: e.target.value })} />
                            <TextField label="Confirmar identidade" fullWidth multiline rows={3} size="small" value={form.confirmIdentityMessage || ''} onChange={(e) => update({ confirmIdentityMessage: e.target.value })} helperText="Use {{nome_cliente}}" />
                            <TextField label="Identidade negada" fullWidth multiline rows={2} size="small" value={form.identityDeniedMessage || ''} onChange={(e) => update({ identityDeniedMessage: e.target.value })} />
                            <TextField label="Consentimento de vinculo" fullWidth multiline rows={3} size="small" value={form.askLinkConsentMessage || ''} onChange={(e) => update({ askLinkConsentMessage: e.target.value })} />
                        </Stack>
                    )}

                    {activeTab === 2 && (
                        <Stack spacing={2}>
                            <TextField label="Introducao da lista" fullWidth multiline rows={2} size="small" value={form.listIntroMessage || ''} onChange={(e) => update({ listIntroMessage: e.target.value })} />
                            <TextField label="Sem faturas" fullWidth multiline rows={2} size="small" value={form.noInvoicesMessage || ''} onChange={(e) => update({ noInvoicesMessage: e.target.value })} />
                            <TextField label="Escolha invalida" fullWidth multiline rows={2} size="small" value={form.invalidChoiceMessage || ''} onChange={(e) => update({ invalidChoiceMessage: e.target.value })} />
                            {form.limitarDiasVencidas && (
                                <TextField label="Faturas expiradas (limite dias)" fullWidth multiline rows={2} size="small" value={form.faturasExpiradasMessage || ''} onChange={(e) => update({ faturasExpiradasMessage: e.target.value })} />
                            )}
                            {form.sendMode !== 'link' && (
                                <TextField label="Sem dados de pagamento (sem solucao)" fullWidth multiline rows={2} size="small" value={form.semDadosPagamentoMessage || ''} onChange={(e) => update({ semDadosPagamentoMessage: e.target.value })} helperText="Quando a fatura nao tiver linha digitavel nem PIX." />
                            )}
                        </Stack>
                    )}

                    {activeTab === 3 && form.sendMode !== 'link' && (
                        <Stack spacing={2}>
                            <Typography variant="caption" color="text.secondary">
                                Mensagens opcionais enviadas junto com os dados de pagamento no modo dados.
                            </Typography>
                            <TextField label="Mensagem 1 (opcional)" fullWidth multiline rows={2} size="small" value={form.mensagem1 || ''} onChange={(e) => update({ mensagem1: e.target.value })} />
                            <TextField label="Mensagem 2 (opcional)" fullWidth multiline rows={2} size="small" value={form.mensagem2 || ''} onChange={(e) => update({ mensagem2: e.target.value })} />
                            <TextField label="Mensagem 3 (opcional)" fullWidth multiline rows={2} size="small" value={form.mensagem3 || ''} onChange={(e) => update({ mensagem3: e.target.value })} />
                            <TextField label="Mensagem 4 (encerramento)" fullWidth multiline rows={2} size="small" value={form.mensagem4 || ''} onChange={(e) => update({ mensagem4: e.target.value })} />
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">Cancelar</Button>
                <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Como funciona o no Consultar Faturas</Typography>
                <IconButton size="small" onClick={() => setHelpOpen(false)}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Typography variant="body2" color="text.secondary">
                        Este no identifica o cliente no WhatsApp, lista as faturas conforme o filtro configurado e envia os dados de pagamento ou o link da fatura automaticamente.
                    </Typography>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>1. Identificacao do cliente</Typography>
                        <Typography variant="body2" component="div" color="text.secondary">
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li>Se o contato ja tem cliente vinculado, pula direto para as faturas.</li>
                                <li>Se nao, pede CPF/CNPJ e busca o cadastro.</li>
                                <li>Exibe o nome encontrado e pede confirmacao (1️⃣ Sim / 2️⃣ Nao).</li>
                                <li>Se negar, oferece informar CPF novamente ou aguardar atendimento.</li>
                                <li>Se confirmar, pergunta se pode vincular o CPF para proximos contatos (1️⃣ Sim vincula / 2️⃣ Nao usa so nesta sessao).</li>
                            </ul>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>2. Listagem e escolha de fatura</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Lista ate a quantidade configurada de faturas no formato:
                        </Typography>
                        <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1, mt: 0.5 }}>
                            1️⃣ - Referencia: 2026-06 - Vencimento: 15/06/2026 - Valor: R$ 99,90
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            O cliente escolhe pelo emoji da lista, pelo numero da opcao, pelo ID da fatura ou pelo mes de referencia (ex: 2026-06 ou 06/2026).
                            <br /><br />
                            Se existir <strong>apenas 1 fatura</strong>, ela e enviada automaticamente, sem pedir escolha.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>3. Modos de envio</Typography>
                        <Typography variant="body2" component="div" color="text.secondary">
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li><strong>Dados:</strong> envia mensagens com valor, vencimento, linha digitavel e PIX (modo padrao).</li>
                                <li><strong>Link:</strong> usa o template WhatsApp do evento <em>enviar_fatura</em> (opcionalmente com PDF).</li>
                            </ul>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>4. Validacoes antes do envio</Typography>
                        <Typography variant="body2" component="div" color="text.secondary">
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li><strong>Limite de dias vencidas:</strong> se ativado, faturas vencidas ha mais de X dias nao sao enviadas. Se todas forem excluidas por esse limite, o fluxo segue pela saida <strong>Sem solucao</strong>.</li>
                                <li><strong>Dados de pagamento (modo Dados):</strong> se a fatura nao tiver linha digitavel nem PIX, o bot informa que nao foi possivel localizar os dados de pagamento e que o atendimento sera transferido — saida <strong>Sem solucao</strong>.</li>
                            </ul>
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>5. Saidas do no (conecte no fluxo)</Typography>
                        <Typography variant="body2" component="div" color="text.secondary">
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li><strong>Sem cliente:</strong> CPF/CNPJ nao encontrado apos as tentativas ou cliente escolheu aguardar atendimento.</li>
                                <li><strong>OK:</strong> fatura enviada com sucesso — conecte ao proximo passo do fluxo.</li>
                                <li><strong>Sem faturas:</strong> cliente identificado, mas nenhuma fatura no filtro configurado.</li>
                                <li><strong>Sem solucao:</strong> faturas ultrapassaram o limite de dias vencidas, ou a fatura selecionada nao possui linha digitavel/PIX no modo Dados — ideal para transferir ao atendente.</li>
                            </ul>
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={() => setHelpOpen(false)} variant="contained" sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}>
                    Entendi
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default FlowBuilderConsultarFaturasModal;
